import React, { useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  useGetCommunityDailyQuery,
  useVoteCommunityDebateMutation,
  useCommentCommunityDebateMutation,
} from "../../store/api/communityApi";
import { communityDebatePath, isVotingClosed } from "../../utils/community";
import { homeSectionPaper } from "../home/homeStyles";
import PlayerHeadshot from "../PlayerHeadshot";
import DebateThread from "./DebateThread";

export const TodaysDebateHero = ({
  sport,
  debate,
  onVote,
  onComment,
  isAuthenticated,
}) => {
  const gated = debate.voteCounts == null;
  const votingClosed = isVotingClosed(debate);
  const columns =
    debate.candidates?.length === 4 ? { xs: "1fr 1fr", md: "1fr 1fr" } : { xs: "1fr", md: "1fr 1fr" };
  return (
    <Box sx={{ ...homeSectionPaper, p: { xs: 2, md: 3 }, mb: 3 }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "0.16em" }}>
        Today's Debate
      </Typography>
      <Typography variant="h4" component="h2" fontWeight={800} sx={{ mt: 0.5 }}>
        {debate.question}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: columns, gap: 2, mt: 3 }}>
        {debate.candidates.map((candidate) => (
          <Box key={candidate.playerId} sx={{ textAlign: "center" }}>
            <PlayerHeadshot playerId={candidate.playerId} sport={debate.sport} size={88} />
            <Typography fontWeight={800} sx={{ mt: 1 }}>
              {candidate.name}
            </Typography>
            {!gated && (
              <Typography color="text.secondary">
                {debate.percents?.[debate.candidates.indexOf(candidate)]}%
              </Typography>
            )}
            <Button
              fullWidth
              sx={{ mt: 1 }}
              disabled={votingClosed}
              onClick={() => onVote(candidate.playerId)}
            >
              {candidate.name}
            </Button>
          </Box>
        ))}
      </Box>
      {gated && (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Vote to see the consensus and join the discussion.
        </Typography>
      )}
      {!gated && (
        <DebateThread
          comments={debate.comments}
          canComment={isAuthenticated}
          onComment={onComment}
        />
      )}
      <Button
        component={RouterLink}
        to={communityDebatePath(sport, debate.slug || debate.id)}
        sx={{ mt: 2 }}
      >
        Open the full debate
      </Button>
    </Box>
  );
};

export const TodaysDebateSection = ({
  sport,
  debate: feedDaily,
  skipDailyFetch = false,
}) => {
  const skip = skipDailyFetch || feedDaily !== undefined;
  const query = useGetCommunityDailyQuery(sport, { skip });
  const debate = skip ? feedDaily : query.data;
  const [vote] = useVoteCommunityDebateMutation();
  const [comment] = useCommentCommunityDebateMutation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [actionError, setActionError] = useState("");

  if (query.isError) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        Today's Debate is unavailable right now.
      </Alert>
    );
  }
  if (!debate) return null;

  const handleVote = async (choiceId) => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    try {
      setActionError("");
      await vote({ id: debate.id, choiceId }).unwrap();
    } catch (err) {
      setActionError(err?.data?.error || "Could not record your vote");
    }
  };

  const handleComment = async (body, parentId) => {
    try {
      setActionError("");
      await comment({ id: debate.id, body, parentId }).unwrap();
    } catch (err) {
      setActionError(err?.data?.error || "Could not post your comment");
      throw err;
    }
  };

  return (
    <>
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError("")}>
          {actionError}
        </Alert>
      )}
      <TodaysDebateHero
        sport={sport}
        debate={debate}
        isAuthenticated={isAuthenticated}
        onVote={handleVote}
        onComment={handleComment}
      />
    </>
  );
};

export default TodaysDebateSection;
